<template>
  <div class="page-container">
    <div class="page-title-container flex items-center">
      <wz-button variant="light" class="mr-3" @click="goBack">
        <i class="ri-arrow-left-line"></i> Volver
      </wz-button>
      <h1 class="page-title mb-0">Detalle de solicitud</h1>
    </div>
    <p v-if="request && request.requestType === 'HISTORICAL'" class="text-muted text-sm mb-3">
      Registro histórico
    </p>

    <div v-if="request">
      <!-- Card: datos del empleado / contrato -->
      <wz-card class="mb-4">
        <template #header>
          <div class="wz-card-title">Datos del colaborador</div>
        </template>
          <div class="flex flex-wrap -mx-2">
            <div class="w-full md:w-1/3 px-2">
              <div class="detail-label text-muted text-sm">Nombre</div>
              <div class="detail-value font-bold">{{ request.employeeName }}</div>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <div class="detail-label text-muted text-sm">Empresa</div>
              <div class="detail-value">{{ request.companyName }}</div>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <div class="detail-label text-muted text-sm">Área</div>
              <div class="detail-value">{{ request.areaName }}</div>
            </div>
          </div>
      </wz-card>

      <!-- Card: datos de la solicitud -->
      <wz-card class="mb-4">
        <template #header-title>Datos de la solicitud</template>
        <template #header-actions>
          <wz-badge v-if="request.requestType === 'VENTA'" variant="secondary" class="badge-purple mr-1" pill>VENTA</wz-badge>
          <wz-badge :variant="getStatusColor(request.status)"
                    :class="{ 'badge-purple': request.status === 'GOZADA' }" pill>{{ request.status }}</wz-badge>
          <wz-badge v-if="showPaymentBadge"
                    :variant="request.isPaid ? 'info-solid' : 'secondary'"
                    :class="{ 'badge-unpaid': !request.isPaid }" pill class="ml-1">
            {{ request.isPaid ? 'PAGADA' : 'SIN PAGAR' }}
          </wz-badge>
        </template>
          <div class="flex flex-wrap -mx-2">
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Inicio período</div>
              <div class="detail-value" v-local-date="request.startDate"></div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Fecha inicio vacaciones</div>
              <div class="detail-value" v-local-date="request.startDate"></div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">Fecha fin vacaciones</div>
              <div class="detail-value" v-local-date="request.endDate"></div>
            </div>
            <div class="w-full md:w-1/4 px-2">
              <div class="detail-label text-muted text-sm">{{ request.requestType === 'VENTA' ? 'Días vendidos' : 'Días calendario' }}</div>
              <div class="detail-value font-bold">{{ request.requestType === 'VENTA' ? (request.soldDays || request.calendarDays) : request.calendarDays }}</div>
            </div>
          </div>
          <div class="flex flex-wrap mt-3 -mx-2" v-if="request.observations">
            <div class="w-full px-2">
              <div class="detail-label text-muted text-sm">Observaciones</div>
              <div class="detail-value">{{ request.observations }}</div>
            </div>
          </div>
      </wz-card>

      <!-- Card: datos del pago (solo si está pagada) -->
      <wz-card class="mb-4" v-if="request.isPaid">
        <template #header-title>Datos del pago</template>
        <template #header-actions>
          <wz-button variant="outline" size="sm" @click="openEditPayment()">
            <i class="ri-edit-line mr-1"></i> Editar datos de pago
          </wz-button>
        </template>
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/4 px-2">
            <div class="detail-label text-muted text-sm">Fecha de pago</div>
            <div class="detail-value">{{ formatDate(request.paidAt) }}</div>
          </div>
          <div class="w-full md:w-1/4 px-2">
            <div class="detail-label text-muted text-sm">Banco</div>
            <div class="detail-value">{{ request.paymentBankName || '-' }}</div>
          </div>
          <div class="w-full md:w-1/4 px-2">
            <div class="detail-label text-muted text-sm">N° de operación</div>
            <div class="detail-value">{{ request.paymentOperationNumber || '-' }}</div>
          </div>
          <div class="w-full md:w-1/4 px-2">
            <div class="detail-label text-muted text-sm">Comprobante</div>
            <div class="detail-value">
              <a v-if="request.hasReceipt" href="#" @click.prevent="downloadReceipt()">
                Descargar <i class="ri-download-2-line"></i>
              </a>
              <span v-else class="text-muted">No adjuntado</span>
            </div>
          </div>
        </div>
        <div class="flex flex-wrap mt-3 -mx-2">
          <div class="w-full md:w-1/4 px-2">
            <div class="detail-label text-muted text-sm">Marcado por</div>
            <div class="detail-value">{{ request.paidBy || '-' }}</div>
          </div>
        </div>
      </wz-card>

      <!-- Card: historial de acciones -->
      <wz-card class="mb-4" v-if="hasHistory">
        <template #header>
          <div class="wz-card-title">Historial de acciones</div>
        </template>
          <div v-if="request.approvedBy" class="history-item mb-3">
            <i class="ri-checkbox-circle-line text-success mr-2"></i>
            <strong>Aprobado</strong> por {{ request.approvedBy }}
            <span v-if="request.approvedAt" class="text-muted ml-2">el {{ request.approvedAt }}</span>
          </div>
          <div v-if="request.rejectionReason" class="history-item mb-3">
            <i class="ri-close-circle-line text-danger mr-2"></i>
            <strong>Rechazado</strong> — Motivo: {{ request.rejectionReason }}
          </div>
          <div v-if="request.cancelledBy" class="history-item mb-3">
            <i class="ri-forbid-line text-warning mr-2"></i>
            <strong>Cancelado</strong> por {{ request.cancelledBy }}
            <span v-if="request.cancelledAt" class="text-muted ml-2">el {{ request.cancelledAt }}</span>
            <div v-if="request.cancellationReason" class="ml-4 text-muted text-sm">
              Motivo: {{ request.cancellationReason }}
            </div>
          </div>
          <div v-if="request.paidBy" class="history-item mb-3">
            <i class="ri-money-dollar-circle-line text-info mr-2"></i>
            <strong>Pagado</strong> por {{ request.paidBy }}
            <span v-if="request.paidAt" class="text-muted ml-2">el {{ formatDate(request.paidAt) }}</span>
          </div>
      </wz-card>

      <!-- Card: documentos adjuntos (cualquier estado) -->
      <wz-card class="mb-4">
        <template #header-title>Documentos adjuntos</template>
        <template #header-actions>
          <wz-button variant="outline" size="sm" @click="triggerDocPicker">
            <i class="ri-add-line mr-1"></i> Agregar documento
          </wz-button>
        </template>

        <input ref="docInput" type="file" accept=".pdf,.jpg,.jpeg,.png"
               style="display:none" @change="onDocSelected" />

        <!-- Formulario de subida pendiente (tras elegir archivo) -->
        <div v-if="pendingDoc" class="flex flex-wrap items-center gap-2 mb-3 p-3"
             style="border:1px dashed var(--admin-border-color); border-radius:6px;">
          <i class="ri-file-text-line"></i>
          <span class="text-sm font-bold mr-2">{{ pendingDoc.name }}</span>
          <input v-model="pendingDescription" type="text" class="wz-form-control" maxlength="100"
                 style="max-width:260px" placeholder="Ej: Solicitud firmada (opcional)" />
          <wz-button size="sm" variant="primary" @click="uploadDoc">Subir</wz-button>
          <wz-button size="sm" variant="outline-secondary" @click="cancelDoc">Cancelar</wz-button>
        </div>

        <p v-if="documents.length === 0 && !pendingDoc" class="text-muted mb-0">No hay documentos adjuntos.</p>

        <div v-if="documents.length" class="overflow-x-auto">
          <table class="wz-table w-full">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Fecha</th>
                <th>Subido por</th>
                <th style="text-align:right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="doc in documents" :key="doc.id">
                <td><i class="ri-file-text-line mr-1"></i>{{ doc.fileName }}</td>
                <td>{{ doc.description || '-' }}</td>
                <td>{{ formatDate(doc.createdAt) }}</td>
                <td>{{ doc.createdBy || '-' }}</td>
                <td style="text-align:right; white-space:nowrap;">
                  <wz-button size="sm" variant="outline" @click="downloadDoc(doc)">
                    <i class="ri-download-2-line"></i>
                  </wz-button>
                  <wz-button size="sm" variant="outline-danger" class="ml-1" @click="deleteDoc(doc)">
                    <i class="ri-close-line"></i>
                  </wz-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </wz-card>

      <!-- Botones de acción -->
      <div class="flex justify-end mt-3">
        <wz-button
          v-if="canEdit"
          variant="outline"
          class="mr-2"
          @click="openEdit()"
        >
          <i class="ri-edit-line mr-1"></i> Editar
        </wz-button>
        <wz-button
          v-if="request.status === 'PENDIENTE'"
          variant="primary"
          class="mr-2"
          @click="openApprove()"
        >
          <i class="ri-checkbox-circle-line mr-1"></i> Aprobar
        </wz-button>
        <wz-button
          v-if="request.status === 'PENDIENTE'"
          variant="danger"
          class="mr-2"
          @click="openReject()"
        >
          <i class="ri-close-circle-line mr-1"></i> Rechazar
        </wz-button>
        <wz-button
          v-if="request.status === 'APROBADA'"
          variant="secondary"
          class="mr-2"
          @click="openEnjoy()"
        >
          <i class="ri-calendar-check-line mr-1"></i> Marcar como gozada
        </wz-button>
        <wz-button
          v-if="canPay"
          variant="primary"
          class="mr-2"
          @click="openPay()"
        >
          <i class="ri-money-dollar-circle-line mr-1"></i> Marcar como pagada
        </wz-button>
        <wz-button
          v-if="canCancel"
          variant="light"
          @click="openCancel()"
        >
          <i class="ri-forbid-line mr-1"></i> Cancelar solicitud
        </wz-button>
        <wz-button
          v-if="request && request.status !== 'CANCELADA'"
          variant="danger"
          class="ml-2"
          @click="showAnnul = true"
        >
          <i class="ri-close-circle-line mr-1"></i> Anular
        </wz-button>
      </div>
    </div>

    <!-- Modal: Editar (fechas + observaciones) -->
    <wz-modal v-model="modalEdit" title="Editar solicitud">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-datepicker label="Fecha de inicio" required v-model="editForm.startDate" />
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-datepicker label="Fecha de fin" required v-model="editForm.endDate" />
        </div>
      </div>
      <div v-if="editCalendarDays !== null" class="mb-3">
        <wz-badge variant="success-solid">{{ editCalendarDays }} días calendario</wz-badge>
      </div>
      <div class="mb-1">
        <wz-textarea label="Observaciones" v-model="editForm.observations" rows="3" placeholder="Observaciones (opcional)" />
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalEdit = false">Cancelar</wz-button>
        <wz-button :disabled="!editValid" @click="onEditSubmit">Guardar cambios</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Aprobar -->
    <wz-modal v-model="modalApprove" title="Aprobar solicitud">
      <p>¿Confirma que desea aprobar esta solicitud de vacaciones?</p>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalApprove = false">Cancelar</wz-button>
        <wz-button @click="onApprove">Aprobar</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Rechazar -->
    <wz-modal v-model="modalReject" title="Rechazar solicitud">
      <div class="mb-3">
        <label>Motivo de rechazo <span class="text-danger">*</span></label>
        <wz-textarea v-model="rejectReason" rows="3" placeholder="Ingrese el motivo del rechazo" />
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalReject = false">Cancelar</wz-button>
        <wz-button variant="danger" :disabled="!rejectReason.trim()" @click="onReject">Rechazar</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Cancelar -->
    <wz-modal v-model="modalCancel" title="Cancelar solicitud">
      <div class="mb-3">
        <label>Motivo de cancelación <span class="text-danger">*</span></label>
        <wz-textarea v-model="cancelReason" rows="3" placeholder="Ingrese el motivo de la cancelación" />
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalCancel = false">Volver</wz-button>
        <wz-button variant="warning" :disabled="!cancelReason.trim()" @click="onCancel">Cancelar solicitud</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Anular por error -->
    <annul-by-mistake-modal
      v-model="showAnnul"
      :submitting="annulSubmitting"
      :entity-description="request ? `${request.employeeName} — vacaciones del ${request.startDate} al ${request.endDate}` : ''"
      @confirm="doAnnul"
    />

    <!-- Modal: Marcar como gozada -->
    <wz-modal v-model="modalEnjoy" title="Marcar como gozada">
      <p>¿Marcar estas vacaciones como gozadas?</p>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalEnjoy = false">Cancelar</wz-button>
        <wz-button @click="onEnjoy">Confirmar</wz-button>
      </template>
    </wz-modal>

    <!-- Modal: Pago (marcar como pagada / editar datos de pago) -->
    <wz-modal v-model="modalPay" :title="payEditMode ? 'Editar datos de pago' : 'Marcar como pagada'">
      <div class="mb-3">
        <wz-datepicker v-model="payForm.paidAt" label="Fecha de pago" required :max-date="today" />
      </div>
      <div class="mb-3">
        <wz-select
          label="Banco"
          required
          :items="bankOptions"
          :value="payForm.bankId"
          defaultOption="Seleccione un banco"
          @change="payForm.bankId = $event"
        />
      </div>
      <div class="mb-3">
        <wz-input
          v-model="payForm.operationNumber"
          label="Número de operación"
          required
          maxlength="50"
          placeholder="N° de operación bancaria"
        />
      </div>
      <div class="mb-3">
        <label class="wz-form-label">
          Comprobante de pago <span class="text-muted text-sm">(opcional · PDF/imagen · máx 10MB)</span>
        </label>
        <input ref="receiptInput" type="file" accept=".pdf,.jpg,.jpeg,.png" @change="onReceiptSelected" />
        <div v-if="payEditMode && request && request.hasReceipt && !payReceiptFile" class="text-muted text-sm mt-1">
          Ya hay un comprobante adjunto. Si subes uno nuevo, reemplazará al anterior.
        </div>
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalPay = false">Cancelar</wz-button>
        <wz-button :disabled="!payValid" @click="onPaySubmit">
          {{ payEditMode ? 'Guardar cambios' : 'Confirmar pago' }}
        </wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert, Util } from "@wizepe/wizeui"
import { VacationService } from '@/core/services'
import { Constants } from '@/core/utils'
import AnnulByMistakeModal from '@/core/components/AnnulByMistakeModal.vue'
import fileDownload from 'js-file-download'
import * as moment from 'moment'

export default {
  name: 'VacationRequestDetail',
  components: { AnnulByMistakeModal },
  data() {
    return {
      request: null,
      documents: [],
      banks: [],
      rejectReason: '',
      cancelReason: '',
      // documentos
      pendingDoc: null,
      pendingDescription: '',
      // pago
      payEditMode: false,
      payForm: { paidAt: '', bankId: null, operationNumber: '' },
      payReceiptFile: null,
      // edición (solo fechas y observaciones)
      editForm: { startDate: '', endDate: '', observations: '' },
      // modales
      modalApprove: false,
      modalReject: false,
      modalCancel: false,
      modalEnjoy: false,
      modalPay: false,
      modalEdit: false,
      showAnnul: false,
      annulSubmitting: false,
    }
  },
  computed: {
    hasHistory() {
      return this.request && (
        this.request.approvedBy ||
        this.request.rejectionReason ||
        this.request.cancelledBy ||
        this.request.paidBy
      )
    },
    canCancel() {
      if (!this.request || this.request.status !== 'APROBADA') return false
      const today = new Date().toISOString().split('T')[0]
      return this.request.startDate > today
    },
    canEdit() {
      return this.request && (this.request.status === 'PENDIENTE' || this.request.status === 'APROBADA')
    },
    editCalendarDays() {
      if (!this.editForm.startDate || !this.editForm.endDate) return null
      const start = new Date(this.editForm.startDate + 'T00:00:00')
      const end   = new Date(this.editForm.endDate   + 'T00:00:00')
      const diff  = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
      return diff > 0 ? diff : null
    },
    editValid() {
      return !!(this.editForm.startDate && this.editForm.endDate && this.editCalendarDays > 0)
    },
    // El badge de pago y el pago aplican a estado APROBADA / GOZADA
    // (los históricos ya muestran su estado real, GOZADA o APROBADA)
    showPaymentBadge() {
      if (!this.request) return false
      return this.request.status === 'APROBADA' || this.request.status === 'GOZADA'
    },
    canPay() {
      return this.request && !this.request.isPaid && this.showPaymentBadge
    },
    today() {
      return new Date().toISOString().slice(0, 10)
    },
    bankOptions() {
      return this.banks.map(b => ({ key: b.id, label: b.name }))
    },
    payValid() {
      return !!(this.payForm.paidAt && this.payForm.bankId &&
        this.payForm.operationNumber && this.payForm.operationNumber.trim())
    },
  },
  methods: {
    formatDate(d) {
      return d ? moment(d).format('DD/MM/YYYY') : '-'
    },
    getStatusColor(status) {
      const found = Object.values(Constants.VacationRequestStatus).find(s => s.key === status)
      return found ? found.color : 'dark'
    },
    // ---- Editar (fechas + observaciones) ----
    openEdit() {
      this.editForm = {
        startDate: this.request.startDate ? moment(this.request.startDate).format('YYYY-MM-DD') : '',
        endDate: this.request.endDate ? moment(this.request.endDate).format('YYYY-MM-DD') : '',
        observations: this.request.observations || '',
      }
      this.modalEdit = true
    },
    async onEditSubmit() {
      if (!this.editValid) return
      this.modalEdit = false
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.updateRequest(this.request.id, {
          startDate: this.editForm.startDate,
          endDate: this.editForm.endDate,
          observations: this.editForm.observations,
        })
        Alert.success('Solicitud actualizada correctamente')
        if (resp && resp.payload && resp.payload.warning) Alert.alert('Advertencia', resp.payload.warning)
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al actualizar la solicitud')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    // ---- Aprobar / Rechazar / Cancelar / Gozada ----
    openApprove() { this.modalApprove = true },
    openReject() { this.rejectReason = ''; this.modalReject = true },
    openCancel() { this.cancelReason = ''; this.modalCancel = true },
    openEnjoy() { this.modalEnjoy = true },
    async onApprove() { this.modalApprove = false; await this.confirmApprove() },
    async onReject() { this.modalReject = false; await this.confirmReject() },
    async onCancel() { this.modalCancel = false; await this.confirmCancel() },
    async doAnnul(reason) {
      try {
        this.annulSubmitting = true
        const resp = await VacationService.annulRequest(this.request.id, reason)
        Alert.success('Vacación anulada')
        if (resp && resp.payload && resp.payload.warning) Alert.alert('Advertencia', resp.payload.warning)
        this.showAnnul = false
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al anular la vacación')
      } finally {
        this.annulSubmitting = false
      }
    },
    async onEnjoy() { this.modalEnjoy = false; await this.confirmEnjoy() },
    async confirmEnjoy() {
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.enjoy(this.request.id)
        Alert.success('Vacaciones marcadas como gozadas')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al marcar como gozada')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmApprove() {
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.approveRequest(this.request.id)
        Alert.success('Solicitud aprobada correctamente')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al aprobar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmReject() {
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.rejectRequest(this.request.id, { rejectionReason: this.rejectReason })
        Alert.success('Solicitud rechazada')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al rechazar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmCancel() {
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.cancelRequest(this.request.id, { cancellationReason: this.cancelReason })
        Alert.success('Solicitud cancelada')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cancelar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    // ---- Pago ----
    openPay() {
      this.payEditMode = false
      this.payForm = { paidAt: this.today, bankId: null, operationNumber: '' }
      this.payReceiptFile = null
      this.resetReceiptInput()
      this.modalPay = true
    },
    openEditPayment() {
      this.payEditMode = true
      this.payForm = {
        paidAt: this.request.paidAt || this.today,
        bankId: this.request.paymentBankId || null,
        operationNumber: this.request.paymentOperationNumber || '',
      }
      this.payReceiptFile = null
      this.resetReceiptInput()
      this.modalPay = true
    },
    onReceiptSelected(e) {
      this.payReceiptFile = (e.target.files && e.target.files[0]) || null
    },
    resetReceiptInput() {
      if (this.$refs.receiptInput) this.$refs.receiptInput.value = ''
    },
    buildPayFormData() {
      const fd = new FormData()
      fd.append('paidAt', this.payForm.paidAt)
      fd.append('bankId', this.payForm.bankId)
      fd.append('operationNumber', this.payForm.operationNumber)
      if (this.payReceiptFile) fd.append('receiptFile', this.payReceiptFile)
      return fd
    },
    async onPaySubmit() {
      if (!this.payValid) return
      const editing = this.payEditMode
      this.modalPay = false
      try {
        this.$store.dispatch('app/loading', true)
        const fd = this.buildPayFormData()
        if (editing) {
          await VacationService.editPayment(this.request.id, fd)
          Alert.success('Datos de pago actualizados')
        } else {
          await VacationService.markPaid(this.request.id, fd)
          Alert.success('Solicitud marcada como pagada')
        }
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al guardar el pago')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadReceipt() {
      try {
        this.$store.dispatch('app/loading', true)
        const response = await VacationService.downloadReceipt(this.request.id)
        fileDownload(response.data, Util.getFileName(response) || 'comprobante-pago')
      } catch (e) {
        Alert.error('Error al descargar el comprobante')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    // ---- Documentos adjuntos ----
    triggerDocPicker() {
      if (this.$refs.docInput) this.$refs.docInput.click()
    },
    onDocSelected(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.pendingDoc = file
      this.pendingDescription = ''
      e.target.value = '' // permite re-seleccionar el mismo archivo luego
    },
    cancelDoc() {
      this.pendingDoc = null
      this.pendingDescription = ''
    },
    async uploadDoc() {
      if (!this.pendingDoc) return
      try {
        this.$store.dispatch('app/loading', true)
        const fd = new FormData()
        fd.append('file', this.pendingDoc)
        if (this.pendingDescription) fd.append('description', this.pendingDescription)
        const resp = await VacationService.uploadDocument(this.request.id, fd)
        this.documents.push(resp.payload)
        this.cancelDoc()
        Alert.success('Documento adjuntado correctamente')
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al subir el documento')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadDoc(doc) {
      try {
        this.$store.dispatch('app/loading', true)
        const response = await VacationService.downloadDocument(this.request.id, doc.id)
        fileDownload(response.data, doc.fileName || Util.getFileName(response))
      } catch (e) {
        Alert.error('Error al descargar el documento')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async deleteDoc(doc) {
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.deleteDocument(this.request.id, doc.id)
        this.documents = this.documents.filter(d => d.id !== doc.id)
        Alert.success('Documento eliminado')
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al eliminar el documento')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    // ---- Carga ----
    async loadDocuments() {
      try {
        const resp = await VacationService.listDocuments(this.$route.params.id)
        this.documents = resp.payload || []
      } catch (e) {
        this.documents = []
      }
    },
    async loadBanks() {
      try {
        const resp = await VacationService.getBanks()
        this.banks = resp.payload || []
      } catch (e) {
        this.banks = []
      }
    },
    // Volver respetando el origen: si se llegó desde la ficha del colaborador (tab Vacaciones),
    // regresar a esa ficha en el mismo tab; si no, al listado global de vacaciones.
    goBack() {
      const { from, employeeId } = this.$route.query
      if (from === 'employee' && employeeId) {
        this.$router.push({ name: 'employee-details',
          params: { id: String(employeeId) }, query: { tab: 'vacations' } })
      } else {
        this.$router.push({ name: 'vacation-requests-list' })
      }
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.getRequest(this.$route.params.id)
        this.request = resp.payload
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar la solicitud')
        this.$router.push({ name: 'vacation-requests-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    await Promise.all([this.loadData(), this.loadBanks()])
    this.loadDocuments()
    // Si se llega desde la lista con ?action=pay, abrir el modal de pago automáticamente
    if (this.$route.query.action === 'pay') {
      if (this.canPay) {
        this.openPay()
      }
      // Limpiar el query param para que no se re-abra al refrescar
      this.$router.replace({ query: {} })
    }
  },
}
</script>
